import {SxProps, Theme} from '@mui/material';
import theme from 'src/theme';
import {PHONE_TOUCH} from 'src/components/mobile/mobileDesign.sx';

export const headerWrapperSx: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    // The phone's app bar: a white 56px band with a hairline under it, bled back out of the shell's
    // padding so the border runs the full width of the screen rather than stopping short of each
    // edge. A desktop keeps the plain breadcrumb row it has always had.
    [theme.breakpoints.down('md')]: {
        height: 56,
        gap: theme.spacing(8 / 8),
        padding: theme.spacing(0, 8 / 8, 0, 4 / 8),
        // The bar is chrome, not content: it spans the screen edge to edge, so it cancels exactly
        // the column's gutter and no more. Two pixels now - the same two the cards keep.
        margin: theme.spacing(-20 / 8, -2 / 8, 20 / 8),
        backgroundColor: theme.palette.common.white,
        borderBottom: `1px solid ${theme.palette.neutral[100]}`,
    },
};

export const headerActionsSx: SxProps<Theme> = {
    maxWidth: theme.spacing(200 / 8),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(10 / 8),
    marginLeft: 'auto',
};

export const textSx = {
    [theme.breakpoints.down('lg')]: {
        fontSize: '0.85rem',
        lineHeight: 1.3,
    },
};

export const breadcrumbsSx: SxProps<Theme> = {
    '& a': {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(5 / 8),
        color: theme.palette.neutral[700],
        fontWeight: 400,
        '& svg': {
            fill: theme.palette.primary.main,
        },
        [theme.breakpoints.down('md')]: {
            // The arrow on its own, in a square a finger can actually hit: where it goes back to is
            // said by the page it goes back to.
            minWidth: theme.spacing(44 / 8),
            minHeight: theme.spacing(44 / 8),
            justifyContent: 'center',
            '& .MuiTypography-root': {
                display: 'none',
            },
        },
    },
};

export const breadcrumbsWrapperSx: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(10 / 8),
    minWidth: 0,
    // This used to disappear below `md`, which left a phone with a page that never said what it was
    // and no way back except the browser's own. It stays now, reduced to the arrow and the page's
    // name: there is no room for both the path taken and the place it arrived at.
    [theme.breakpoints.down('md')]: {
        gap: theme.spacing(6 / 8),
        flex: 1,
    },
};

export const separateIconSx: SxProps<Theme> = {
    fill: theme.palette.neutral[500],
    width: theme.spacing(20 / 8),
    // Nothing to separate once the trail is down to one name.
    [theme.breakpoints.down('md')]: {
        display: 'none',
    },
};

export const currentPageSx = {
    color: theme.palette.neutral[500],
    fontWeight: 400,
    minWidth: 0,
    // On a phone this is the page's title rather than the tail of a trail, so it is read first and
    // shortens rather than pushing the menu button off the edge.
    [theme.breakpoints.down('md')]: {
        color: theme.palette.text.primary,
        fontSize: '17px',
        fontWeight: 600,
        letterSpacing: '-0.01em',
        lineHeight: 1.1,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
};

/**
 * The page's name, and under it the page it was opened from.
 *
 * A column rather than a row: on a phone the trail to the left is down to an arrow, so the place it
 * leads back to has to be said in words somewhere, and there is only one line spare to say it on.
 */
export const titleWrapperSx: SxProps<Theme> = {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    minWidth: 0,
    [theme.breakpoints.down('md')]: {
        flexGrow: 1,
        gap: theme.spacing(2 / 8),
    },
};

/** The second line. The same message the back link carries, shown only where the trail is not. */
export const contextLineSx: SxProps<Theme> = {
    display: 'none',
    [theme.breakpoints.down('md')]: {
        display: 'block',
        fontSize: '11px',
        fontWeight: 500,
        lineHeight: 1.1,
        color: theme.palette.neutral[500],
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
};

export const buttonWrapperSx: SxProps<Theme> = {
    '& button': {
        marginLeft: 0,
    },
    // A finger's target rather than the theme's filled circle: inside the bar the button reads as
    // chrome, and 44px is the floor everything below `md` is built to.
    [theme.breakpoints.down('md')]: {
        flexShrink: 0,
        '& button': {
            width: PHONE_TOUCH,
            height: PHONE_TOUCH,
            borderRadius: theme.spacing(12 / 8),
            backgroundColor: 'transparent',
            '& svg': {
                fontSize: '22px',
                fill: theme.palette.text.primary,
            },
        },
    },
    [theme.breakpoints.up('md')]: {
        display: 'none',
    },
};
